import { useCallback, useState } from "react";

import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";
import { confirmLeaveNavigation } from "../utils/confirmNavigation";

interface UseGuardedModalCloseOptions {
  id: string;
  open: boolean;
  hasChanges: boolean;
  onClose: () => void;
  message?: string;
}

export const useGuardedModalClose = ({
  id,
  open,
  hasChanges,
  onClose,
  message,
}: UseGuardedModalCloseOptions) => {
  const [localDirty, setLocalDirty] = useState(false);
  const dirty = hasChanges || localDirty;

  const { markSaved } = useUnsavedChangesGuard({
    id,
    when: open && dirty,
    message,
  });

  const markModalSaved = useCallback(() => {
    markSaved();
    setLocalDirty(false);
  }, [markSaved]);

  const requestClose = useCallback(async () => {
    if (open && dirty) {
      const canClose = await confirmLeaveNavigation(message);
      if (!canClose) {
        return false;
      }
    }

    markSaved();
    setLocalDirty(false);
    onClose();
    return true;
  }, [open, dirty, message, markSaved, onClose]);

  const onValuesChange = useCallback(() => {
    setLocalDirty(true);
  }, []);

  return {
    requestClose,
    markModalSaved,
    onValuesChange,
  };
};
